import { count, sentence, whom } from '../core/words.js';
import { formatSpan, formatTime } from './clock.js';
import { wantedAnchors } from './grading.js';
import { roomNeeds } from './roomNeeds.js';
import { PLACES, SHORTFALLS } from '../data/resources.js';
import { gateFor } from '../data/staff.js';

/**
 * The one line that always says where to go next, and the errands under it.
 *
 * Every state also says `where`, as a prop id or a room id: the place the
 * line names, for the minimap to mark. The same branch decides both, so the
 * map can never point somewhere the words do not.
 *
 * Read off the shift, the board, the room and the task list, and nothing
 * else; it decides nothing and changes nothing. Moved out of `main.js` whole.
 */
export function createObjective({ beos, clock, setup, shift, standing, tasks, level, writeups = null, stores = null, dressed = () => true, carting = () => true, howFor = () => null, whoFor = () => null, learner = () => false, loose = () => 0, gone = () => new Set(), stray = () => null, holding = () => ({ kind: null, count: 0 }) }) {
  function errandLines() {
    const now = clock.day * 1440 + clock.minutes;
    return tasks.list.map((task) => {
      const left = Math.round(task.dueAbsolute - now);
      return {
        label: task.label,
        left: left >= 0 ? formatSpan(left) : 'gone',
        tone: left < 0 ? 'bad' : left < 25 ? 'warn' : 'idle',
      };
    });
  }

  /** A room's name as a person would say it, for a line that has to name a place. */
  function roomName(id) {
    return level.rooms.find((room) => room.id === id)?.name ?? 'ballroom';
  }

  /**
   * The step or two after the one on the line, smaller, so a player is never
   * stopped at a waypoint wondering what it was for. Each names a person, a
   * place or a key, like the line. In order: anything short and not yet
   * covered (who to see), anything covered and waiting to be fetched (where),
   * then the room itself. Filing it is the submit line's, already on screen.
   */
  /*
   * Rounds, then chairs, then linen: stock for the room is fetched when its
   * turn comes. Told to fetch the linen first, a new starter carried it to the
   * room, went for rounds with it still in their arms, and the rounds took its
   * place; the room's own white linen went back on the shelf and they laid
   * ivory. Anything that is not rounds, chairs or linen is fetched when ready.
   */
  function fetchNow(row, spec) {
    const stages = ['round', 'chair', 'linen'];
    if (!spec || !row || !stages.includes(row.item)) return true;
    const now = (setup.tables && roomNeeds(setup, spec, gone())?.stage) || 'linen';
    return stages.indexOf(row.item) <= stages.indexOf(now);
  }

  /*
   * Each step is the line it has always been (`say`, kept for the panel that
   * lists them and for the scripts) and the same thing as a task card would
   * put it (`task`): what to do, in plain words, and one short line why.
   */
  function step(say, doLine, why, progress = null) {
    return { say, task: { do: doLine, why, progress } };
  }

  function stepsFor(active, spec) {
    const out = [];
    const held = stray();
    if (held) out.push(step(`Your hands are full of the ${held}: put it down here ([G], a short tap) to pick anything else up`, `Put down the ${held}: tap [G]`, 'Your hands are full, so nothing else can be picked up'));
    /** Said after the room's own step: nothing to walk to. */
    const later = [];
    for (const entry of active.beo.shortfalls ?? []) {
      if (entry.resolved) continue;
      const row = SHORTFALLS[entry.id];
      if (!row) continue;
      const gate = gateFor(row.owner);
      const how = howFor(entry);
      const wants = row.wants ?? row.label.toLowerCase();
      // Nothing to be done about it today: said, and not the next step.
      if (how && /^nobody/.test(how)) {
        const what = wants.replace(/^the /, '');
        later.push(step(`No ${what} today: ${how.replace(/^nobody will cover it today: /, '')}`, `Carry on without the ${what}`, 'Nobody will cover it today; the room can be filed without it'));
        continue;
      }
      const who = whoFor(entry) ?? gate?.name;
      out.push(
        who
          ? step(`Needs ${wants}: see ${whom(who)}, ${how ?? 'to ask, trade or take'}`, `Ask ${whom(who)} for ${wants}`, `The room is short of it. ${sentence(how ?? 'talk to them with [E]')}`)
          : step(`Needs ${wants}: find who has it`, `Find who has ${wants}`, 'The room is short of it')
      );
    }
    if (stores) {
      for (const entry of active.beo.shortfalls ?? []) {
        if (!entry.resolved) continue;
        const row = SHORTFALLS[entry.id];
        if (!fetchNow(row, spec)) continue;
        for (const place of Object.values(row?.at ?? {})) {
          if (!stores.waitingAt(place)) continue;
          const where = PLACES[place]?.where ?? 'where it was left';
          out.push(step(`Fetch ${row.wants} from ${where}`, `Collect ${row.wants} from ${where}`, 'It was left there for you; press [E] at it'));
          break;
        }
      }
    }
    const needs = spec && setup.tables ? roomNeeds(setup, spec, gone()) : null;
    if (needs?.missing.length && needs.offPlan.length) {
      out.push(step(
        `${needs.offPlan.length === 1 ? 'A round is' : `${needs.offPlan.length} rounds are`} off the plan: [E] at it to strike it, chairs and cloth first, then set it on the empty ring`,
        `Move the table that is off the plan: [E] at it to take it apart`,
        'Chairs and cloth come off first, then set it on the empty gold ring',
      ));
    }
    // Some of what the room wants is on the floor somewhere: said, because the
    // floor guide goes there (`nextStepPlace` in `main.js`).
    if (needs?.stage && needs.missing.length + needs.fewChairs.length + needs.bare.length > 0) {
      const n = loose(needs.stage);
      if (n > 0) {
        const name = { round: ['round', 'rounds'], chair: ['chair', 'chairs'], linen: ['linen', 'linens'] }[needs.stage];
        const plain = { round: ['round table', 'round tables'], chair: ['chair', 'chairs'], linen: ['tablecloth', 'tablecloths'] }[needs.stage];
        out.push(step(
          `${n === 1 ? `A ${name[0]} is` : `${n} ${name[1]} are`} lying on the floor: pick ${n === 1 ? 'it' : 'them'} up and set ${n === 1 ? 'it' : 'them'}, the arrows go there`,
          `Pick up the ${n === 1 ? plain[0] : `${n} ${plain[1]}`} lying on the floor`,
          'The room needs them; the arrows go there',
        ));
      }
    }
    const c = setup.counts ?? {};
    if (needs?.stage === 'chair' && stores?.available && stores.available('chair') <= 0) {
      const spare = [...wantedAnchors(spec)].some((key) => (setup.tableAt(key)?.seats ?? 0) > spec.chairsPerTable);
      if (spare) out.push(step('No chairs left on the shelf: [hold E] at a round with too many to pull one, then set it where one is short', 'Take a spare chair from a table with too many: [hold E] at it', 'The shelf is out of chairs; set it where one is short'));
    }
    if (needs?.stage === 'chair' && (c.chairs ?? 0) >= spec.tableCount * spec.chairsPerTable) {
      // The total says the chairs are done; one round is still short.
      const short = setup.tableAt(needs.fewChairs[0]);
      out.push(step(
        `${needs.fewChairs.length === 1 ? 'A round is' : `${needs.fewChairs.length} rounds are`} short of chairs (${short?.seats ?? 0} of ${spec.chairsPerTable} at one): the arrows go to it`,
        `Set chairs at the table that is short`,
        `It has ${short?.seats ?? 0} of ${spec.chairsPerTable}; the arrows go to it`,
      ));
    }
    if (spec?.linen) {
      /*
       * A cloth of the wrong colour counts as a cloth on every counter, so a
       * room dressed in ivory for a hunter sheet read "6 of 6 rounds set" and
       * nothing else, and a new starter stood in it waiting to be told.
       */
      const wrong = [...wantedAnchors(spec)].filter((key) => {
        const cover = setup.tableAt(key)?.cover;
        return cover && cover.variant !== spec.linen;
      }).length;
      if (wrong) {
        out.push(step(
          `${wrong === 1 ? 'One round has' : `${wrong} rounds have`} the wrong linen: the sheet wants ${spec.linen}. [hold E] at the round to strip it, then lay ${spec.linen} (the rack's colour is a [hold E] at the rack)`,
          `Take the wrong cloth off: [hold E] at the table`,
          `The sheet wants ${spec.linen}; [hold E] at the linen rack changes its colour`,
        ));
      }
    }
    if (spec) {
      // A room is more chairs than two arms: said while you are not pushing a
      // cart and the room is not yet half set, because a cart is one trip.
      const early = (setup.counts?.tables ?? 0) * 2 < spec.tableCount;
      out.push(step(
        !carting() && early && spec.tableCount * spec.chairsPerTable > 8
          ? `Set ${count(spec.tableCount, 'round')} as the sheet shows ([F]); a table cart from banquet storage carries a room in one trip`
          : `Set ${count(spec.tableCount, 'round')} as the sheet shows ([F]); [C] sets what you carry on the nearest spot`,
        ...stageTask(active, spec, needs),
      ));
    }
    return [...out, ...later];
  }

  /**
   * The room's own step as a task: what is owed first, rounds, then chairs,
   * then cloth, with how far along that part is. Bring them while your hands
   * are empty; set them once they are full of it.
   */
  function stageTask(active, spec, needs) {
    const room = roomName(active.beo.room);
    const wanted = [...wantedAnchors(spec)];
    const at = (key) => setup.tableAt(key);
    const hands = holding() ?? {};
    const has = (kind) => hands.kind === kind && hands.count > 0;
    const due = `due ${formatTime(active.times.setupBy)}`;
    const linen = spec.linen ?? 'white';
    switch (needs?.stage ?? 'round') {
      case 'chair': {
        const seated = wanted.reduce((n, key) => n + Math.min(at(key)?.seats ?? 0, spec.chairsPerTable), 0);
        return [has('chair') ? 'Set a chair at each round table: [E] beside it' : `Bring chairs from Banquet Storage to the ${room}`, `${spec.chairsPerTable} at each table, then ${linen} cloths · ${due}`, `${seated} of ${spec.tableCount * spec.chairsPerTable}`];
      }
      case 'linen': {
        const dressed = wanted.filter((key) => at(key)?.cover?.variant === spec.linen).length;
        return [has('linen') ? `Lay a ${linen} cloth on each table: [E] beside it` : `Bring ${linen} tablecloths from Banquet Storage`, `The last part of the room · ${due}`, `${dressed} of ${spec.tableCount}`];
      }
      default: {
        const standing = wanted.filter((key) => at(key)).length;
        return [has('round') ? 'Set the round table on a gold ring: [E] there' : `Bring round tables from Banquet Storage to the ${room}`, `${spec.tableCount} tables, ${spec.chairsPerTable} chairs at each, ${linen} cloths · ${due}`, `${standing} of ${spec.tableCount}`];
      }
    }
  }

  /**
   * The one line that always says where to go next.
   *
   * It used to return null whenever there was no accepted event, which is most of
   * the start of every shift and the whole of the case it was needed for: you can
   * be forty minutes late for a shift you never clocked into with nothing on
   * screen to say so, let alone where the clock is. A block that appears once you
   * already know what you are doing is a block for somebody who does not need it.
   *
   * So there are four states and one rule about all of them: **name a thing, not
   * a state.** "No event accepted" is a description of the world and tells you
   * nothing; "Read the board in the muster alcove" is somewhere to walk. Every
   * branch below ends in a place or a key.
   */
  function objectiveFor() {
    const status = shift.status(clock);
    const active = beos.active;

    if (!active) {
      /*
       * Not on the clock yet, and the only thing in the building that matters is
       * a box on a wall in the alcove. How late says itself: a number counting
       * down is a different thing to read than a number counting up, and the
       * second one is the one that gets people fired.
       */
      /*
       * In your own clothes, the clock will not take you, so the clock is not
       * where to go: the locker is. The line, the mark and the route all go
       * there first, and the clock is the step after.
       */
      if ((status.canClockIn || status.phase === 'before') && !dressed()) {
        const late = status.canClockIn && status.untilStart < 0;
        return {
          errands: errandLines(),
          title: late ? 'Late' : 'Start of shift',
          where: { prop: 'break-lockers' },
          line: 'Change into your uniform at your locker, in the break room',
          next: ['Then clock in at the time clock, in the muster alcove'],
          task: { do: 'Change into your uniform at your locker, in the break room', why: late ? `Late: the shift began ${formatTime(status.start)}. The time clock will not take you in your own clothes` : 'The time clock will not take you in your own clothes' },
          tone: late ? 'bad' : 'warn',
          progress: late
            ? `${formatSpan(-status.untilStart)} past the start. Shift began ${formatTime(status.start)}`
            : `Shift starts ${formatTime(status.start)}`,
        };
      }
      if (status.canClockIn) {
        const late = status.untilStart < 0;
        return {
          errands: errandLines(),
          title: late ? 'Late' : 'Start of shift',
          where: { prop: 'time-clock' },
          line: 'Clock in at the time clock, in the muster alcove',
          next: ['Then take an event off the BEO board beside it'],
          task: {
            do: 'Clock in at the time clock, in the muster alcove',
            why: late
              ? status.phase === 'missed' ? 'You missed the start of your shift' : `Late: your shift began ${formatTime(status.start)}`
              : `Your shift starts at ${formatTime(status.start)}`,
          },
          tone: late ? 'bad' : 'warn',
          progress: late
            ? status.phase === 'missed' && writeups
              ? `NO SHOW · written up for it, ${writeups.count} of ${writeups.max} on your card`
              : `${formatSpan(-status.untilStart)} past the start. Shift began ${formatTime(status.start)}`
            : `Shift starts ${formatTime(status.start)} · ${formatSpan(status.untilStart)} to get there`,
        };
      }
      if (status.phase === 'before') {
        return {
          errands: errandLines(),
          title: 'Before the shift',
          where: { prop: 'time-clock' },
          line: `Be at the time clock by ${formatTime(status.start)}`,
          next: ['Clock in, then take an event off the BEO board beside it'],
          task: { do: `Be at the time clock by ${formatTime(status.start)}`, why: `It opens at ${formatTime(status.opens)}` },
          tone: 'idle',
          progress: `The clock opens ${formatTime(status.opens)}`,
        };
      }
      if (status.phase === 'missed') {
        // The window shut. There is still one thing to do and it is not the clock.
        return {
          errands: errandLines(),
          title: 'Shift missed',
          where: { prop: 'break-cot' },
          line: 'Sleep on the cot in the break room, and start tomorrow',
          task: { do: 'Sleep on the cot in the break room', why: 'The shift is over for today; start again tomorrow' },
          tone: 'bad',
          progress: writeups
            ? `Written up for it · ${writeups.count} of ${writeups.max} on your card`
            : 'The late window closed. Nothing else opens today.',
        };
      }
      if (status.phase === 'over') {
        return {
          errands: errandLines(),
          title: 'Off the clock',
          where: { prop: 'break-cot' },
          line: 'Sleep on the cot in the break room',
          task: { do: 'Sleep on the cot in the break room', why: `Your shift ended at ${formatTime(status.end)}` },
          tone: 'idle',
          progress: `Shift ended ${formatTime(status.end)}`,
        };
      }
      /*
       * A new starter's first days: one room is the day's work. Told "then the
       * board, for another event", a new starter took a second room with an
       * hour and a half on it, missed it, and was written up. The board is
       * still there for anybody who wants it; the line says what it costs.
       */
      const filedToday = () => (beos.history ?? []).some((record) => record.day === clock.day && record.closedAt != null);
      if (learner() && filedToday()) {
        return {
          errands: errandLines(),
          title: 'That is the day\'s work',
          where: { prop: 'time-clock' },
          line: 'Clock out at the time clock, in the muster alcove',
          next: ['The board has more if you want it, but a room that misses its deadline is a write-up'],
          task: { do: 'Clock out at the time clock, in the muster alcove', why: 'One room is the day\'s work on your first days' },
          tone: 'good',
          progress: `Shift ends ${formatTime(status.end)}`,
        };
      }
      /*
       * On the clock with an empty board in front of you. The board is the only
       * thing that can change that, so the board is what it says.
       */
      return {
        errands: errandLines(),
        title: 'On the clock',
        where: { prop: 'beo-board' },
        line: 'Read the BEO board in the muster alcove and take an event',
        next: ['Then hold [F] to read its sheet: the room, the plan, and what it is short of'],
        task: { do: 'Take a room off the BEO board, in the muster alcove', why: 'Each card is a room to set up; pick one there is time for' },
        tone: 'warn',
        progress: `${beos.board(clock).length} posted · shift ends ${formatTime(status.end)}`,
      };
    }

    const remaining = beos.remaining(clock);
    /*
     * Everything the sheet asked for is standing in the room, so the next thing
     * is not another round, it is the key that ends it. Said in the headline
     * rather than only in the submit line underneath, because at that moment the
     * headline is still counting down a deadline you have already beaten.
     */
    const spec = active.beo.setup;
    // A card that is not a room says what it is and nothing about rounds.
    if (!spec) {
      return {
        errands: errandLines(),
        title: `${active.beo.code} · ${active.beo.title}`,
        where: active.beo.room ? { room: active.beo.room } : null,
        line: remaining >= 0 ? `Due by ${formatTime(active.times.setupBy)} · ${formatSpan(remaining)} left` : `OVERDUE by ${formatSpan(-remaining)}`,
        task: { do: active.beo.title, why: remaining >= 0 ? `Due by ${formatTime(active.times.setupBy)}` : `Late by ${formatSpan(-remaining)}` },
        tone: remaining < 0 ? 'bad' : 'warn',
        progress: active.beo.note ?? '',
      };
    }
    /*
     * Counted the way `grading.js` counts it, on the anchors the layout asked
     * for. `spec.linen` is the colour rather than a number of them, which is a
     * thing you find out by writing `counts.linens >= spec.linen` and watching a
     * finished room never once say it was finished.
     */
    const wanted = [...wantedAnchors(spec)];
    const standing = wanted.filter((key) => {
      const table = setup.tableAt(key);
      return (
        table &&
        table.seats >= spec.chairsPerTable &&
        table.cover?.variant === spec.linen
      );
    }).length;
    const done = standing >= spec.tableCount;
    /*
     * Or as set as it can be: what it still wants nobody has today (`gone`).
     * Said as the headline, like a finished room, with what it is short of.
     */
    const asItStands = !done && setup.tables ? roomNeeds(setup, spec, gone()) : null;
    if (asItStands && !asItStands.stage && !asItStands.offPlan.length && asItStands.short.length) {
      const names = { round: 'rounds', chair: 'chairs', linen: 'linen' };
      return {
        errands: errandLines(),
        title: `${active.beo.code} · ${active.beo.title}`,
        where: active.beo.room ? { room: active.beo.room } : null,
        line: `File the room with [R], in the ${roomName(active.beo.room)}: it is as set as it can be without the ${asItStands.short.map((k) => names[k]).join(' and ')}`,
        next: ['Nobody has any more today; a room filed short beats one the doors find unfinished'],
        task: { do: 'File the room as it is: press [R]', why: `Nobody has any more ${asItStands.short.map((k) => names[k]).join(' or ')} today`, progress: `${standing} of ${spec.tableCount}` },
        tone: 'warn',
        progress: `${standing} of ${count(spec.tableCount, 'round')} standing, dressed and seated`,
        submit: `[R]  file the room  ·  short of ${asItStands.short.map((k) => names[k]).join(' and ')} nobody has`,
      };
    }
    if (done) {
      return {
        errands: errandLines(),
        title: `${active.beo.code} · ${active.beo.title}`,
        where: active.beo.room ? { room: active.beo.room } : null,
        line: `File the room with [R], in the ${roomName(active.beo.room)}`,
        next: [learner() ? 'Then the time clock: on your first days one room is the day\'s work' : 'Then the board, for another event, or the time clock at the end of the shift'],
        task: { do: `File the room: press [R] in the ${roomName(active.beo.room)}`, why: 'Everything on the sheet is set', progress: `${standing} of ${spec.tableCount}` },
        tone: remaining < 0 ? 'warn' : 'good',
        progress: `${standing} of ${count(spec.tableCount, 'round')} standing, dressed and seated`,
        submit: '[R]  file the room  ·  everything the sheet asked for is standing',
      };
    }
    const steps = stepsFor(active, spec);
    return {
      errands: errandLines(),
      title: `${active.beo.code} · ${active.beo.title}`,
      where: active.beo.room ? { room: active.beo.room } : null,
      /*
       * Names the room, not only the hour. "Set by 7:00 PM" is a fact about the
       * sheet; where to walk is the thing this line is for, and a player who has
       * just taken an event off the board has not necessarily been told which
       * room it is in.
       */
      line:
        remaining >= 0
          ? `Set the ${roomName(active.beo.room)} by ${formatTime(active.times.setupBy)} · ${formatSpan(remaining)} left`
          : `OVERDUE by ${formatSpan(-remaining)} · the ${roomName(active.beo.room)} is not set`,
      next: steps.slice(0, 2).map((entry) => entry.say),
      task: late(steps[0].task, remaining, active),
      // Every step as a task, in order, for a caller that has to pass one by.
      tasks: steps.map((entry) => late(entry.task, remaining, active)),
      tone: remaining < 0 ? 'bad' : remaining < 60 ? 'warn' : 'good',
      progress: `${count(active.beo.setup.tableCount, 'round')} · ${count(active.beo.setup.chairsPerTable, 'chair')} each · ${active.beo.setup.linen} linen`,
      /*
       * Where the key that files it is named, and the only place it needs to be.
       *
       * It has no prompt, because a prompt is what the action key is about to do
       * and this is not on the action key. It belongs with the event instead —
       * the block already on screen saying which room, by when, and how much of
       * it is set. A verb that ends a thing belongs next to the thing.
       */
      submit: `[R]  file the room  ·  ${setup.counts.tables} of ${count(active.beo.setup.tableCount, 'round')} set`,
    };
  }

  /** A task's why, once the room is late: that, and the doors. */
  function late(task, remaining, active) {
    if (remaining >= 0) return task;
    return { ...task, why: `Late by ${formatSpan(-remaining)}: the doors open at ${formatTime(active.times.doors ?? active.times.setupBy + 30)}` };
  }

  return { objectiveFor, errandLines, roomName };
}
